import { CourseDetail } from "@/routes/course/types/courseDetail.ts";
import { HomepageLoaderData } from "@/routes/homePage/types/homePageDTO";
import { RegisterFormData } from "@/routes/guestRoutes/register/types/registerSchema";
import { API_CONFIG } from "@/utils/constants";
import { middleware } from "./middleware";
import i18n from "@/i18n/config";
import { TokenResponse } from "@/types/apiTypes/tokenResponse";
import { ApiResponse } from "@/types/apiTypes/apiResponse";
import { UserInformationData } from "@/types/auth/userInformationData";
import { studentAccountInfo } from "@/routes/settings/accountInfo/types/studentAccountInfo";
import { changePasswordRequest } from "@/routes/settings/password/types/changePasswordRequestSchema.ts";
import { errorHandler } from "../errorHandler/errorHandler";
import { HttpError } from "@/types/errorTypes/httpError";
import { eventBus } from "../event/eventBus";
import {
  lecturerManageCoursesLoaderDTO,
  LecturerGetCoursesRequest,
} from "@/routes/lecturer/routes/manageCourses/types/lecturerManageCourse";
import {
  LecturerOrderItem,
  LecturerGetOrdersRequest,
} from "@/routes/lecturer/routes/finance/types/lecturerFinanceTypes";
import { AdminPanelCategoryData } from "@/routes/adminPanel/categories/main/types/adminPanelCategoryData.ts";
import { BasePaginatedResponse } from "@/types/apiTypes/basePaginatedReponse.ts";
import { BasePaginatedRequest } from "@/types/apiTypes/basePaginatedRequest.ts";
import { AdminPanelCategoryCreateData } from "@/routes/adminPanel/categories/create/types/adminPanelCategoryCreateData.ts";
import { baseCreateResponse } from "@/types/apiTypes/baseCreateResponse.ts";
import { AdminPanelUserData } from "@/routes/adminPanel/users/main/types/adminPanelUserData.ts";
import { AdminPanelUserDetailData } from "@/routes/adminPanel/users/edit/types/adminPanelUserDetailData.ts";
import { UserCoursesData } from "@/routes/adminPanel/users/courses/types/userCoursesData.ts";
import { AdminPanelTagData } from "@/routes/adminPanel/tags/main/types/adminPanelTagData";
import { AdminPanelTagCreateData } from "@/routes/adminPanel/tags/create/types/adminPanelTagCreateData.ts";
import { AdminPanelDashboardLoaderData } from "@/routes/adminPanel/dashboard/types/adminPanelDashboardLoaderData.ts";
import { ResetPasswordDTO } from "@/routes/guestRoutes/resetPassword/types/resetPasswordTypes";
import { VerifyCodeFormData } from "@/routes/guestRoutes/verifyCode/types/verifyCodeTypes";
import {
  ManageCourseCreateSectionDTO,
  ManageCourseUpdateSectionDTO,
  reOrderDataDTO,
} from "@/routes/lecturer/routes/manageCourseContent/types/manageCourseActionTypes";
import { ManageCourseData } from "@/routes/lecturer/routes/manageCourseContent/types/manageCourseContentTypes";
import {
  lecturerCreateCourseCategory,
  lecturerCreateCourseTag,
} from "@/routes/lecturer/routes/createCourse/types/lecturerCreateCourseTypes";
import { CoursesFilter } from "@/routes/courses/types/coursesFilter.ts";
import { BaseFilterOptionResponse } from "@/types/apiTypes/baseFilterOptionResponse.ts";
import { BasketDto } from "@/routes/basket/types/basketDto";
import { AddItemRequest } from "@/routes/basket/types/apiTypes/addItemRequest";
import { RemoveItemRequest } from "@/routes/basket/types/apiTypes/removeItemRequest";
import { ApplyDiscountCodeRequest } from "@/routes/basket/types/apiTypes/applyDiscountCodeRequest";
import { ApplyDiscountCodeToBasketItemRequest } from "@/routes/basket/types/apiTypes/applyDiscountCodeToBasketItemRequest";
import { RemoveDiscountCodeFromBasketItemRequest } from "@/routes/basket/types/apiTypes/removeDiscountCodeFromBasketItemRequest";
import { RemoveDiscountCodeRequest } from "@/routes/basket/types/apiTypes/removeDiscountCodeRequest";
import { CreateFreeOrderRequest } from "@/routes/basket/types/apiTypes/createFreeOrderRequest.ts";
import { OrderSummaryDto } from "@/routes/orders/summary/types/orderSummaryDto.ts";
import { MyCoursesCourseCardDto } from "@/routes/myCourses/types/myCoursesCourseCardDto.ts";
import { CourseLearningDto } from "@/routes/course/learn/types/courseLearningDto.ts";
import { PaymentInitRequest } from "@/types/payment/paymentInitRequest";
import { PaymentInitResponse } from "@/types/payment/paymentInitResponse";
import { PaymentStatusDto } from "@/types/payment/paymentStatusDto";
import { OrderHistoryDto } from "@/types/payment/orderHistoryDto";
import { DiscountCodeData } from "@/routes/lecturer/routes/discountCode/main/types/discountCodeData.ts";
import { DiscountCodeCreateData } from "@/routes/lecturer/routes/discountCode/create/types/discountCodeCreateData.ts";
import { CoursesForDiscountCodeDto } from "@/routes/lecturer/routes/discountCode/main/types/coursesForDiscountCodeDto.ts";
import { AdminPanelDiscountCodeData } from "@/routes/adminPanel/discountCodes/main/types/adminPanelDiscountCodeData.ts";
import { AdminPanelDiscountCodeCreateData } from "@/routes/adminPanel/discountCodes/create/types/adminPanelDiscountCodeCreateData.ts";
import { CourseDto } from "@/routes/courses/types/coursesDTO.ts";
import {
  ManageCourseUpdateLectureLoaderDTO,
  ManageCourseUpdateSectionLoaderDTO,
} from "@/routes/lecturer/routes/manageCourseContent/types/manageCourseLoaderTypes";
import { lecturerManageCourseLoaderDTO } from "@/routes/lecturer/routes/manageCourseInfo/types/manageCourseInfoTypes";
import { CertificateResponse } from "@/routes/certificates/types/certificateResponse";
import { UpdateBookmarkGroupRequest } from "@/routes/bookmarks/types/updateBookmarkGroupRequest.ts";
import { BookmarkGroupData } from "@/routes/bookmarks/types/bookmarkGroupData.ts";
import { CreateBookmarkGroupRequest } from "@/routes/bookmarks/types/createBookmarkGroupRequest";
import { AddLectureToBookmarkGroupRequest } from "@/routes/bookmarks/types/addLectureToBookmarkGroupRequest.ts";
import { RemoveLectureFromBookmarkGroupRequest } from "@/routes/bookmarks/types/removeLectureFromBookmarkGroupRequest.ts";
import {
  CourseSidebar,
  LectureContent,
} from "@/routes/course/lecture/types/lectureTypes";
import {
  AdminManageCourseResponse,
  PagingResultModel,
} from "@/routes/adminPanel/manageCourses/types/adminManageCoursesTypes";
import { adminManageCoursesInDetailLoaderDTO } from "@/routes/adminPanel/manageCoursesInDetail/manageCourses/types/adminManageCoursesInDetailTypes";

export class ApiClient {
  private middleware: middleware;
  constructor(baseURL: string) {
    this.middleware = new middleware(baseURL);
  }

  async get<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await this.middleware.fetch(url, {
      ...options,
      method: "GET",
    });
    // fetch HTTP hatalarını throw etmez, 404 dönmüş olsa bile fetch başarıyla tamamlanmış bir Promise döndürür, çünkü bağlantı kurulmuştur.
    if (!response.ok) {
      await this.handleApiException(response);
    }
    return response.json() as Promise<T>;
  }

  async getHtmlFromBlob(
    url: string,
    options: RequestInit = {},
  ): Promise<string> {
    const response = await fetch(url, {
      ...options,
      method: "GET",
      headers: {
        Accept:
          "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
        "Accept-Language": "tr-TR,tr;q=0.9,en;q=0.8",
        "Accept-Encoding": "gzip, deflate, br",
        ...options.headers, // Mevcut header'ları override etmek için
        mode: "cors",
      },
    });
    // fetch HTTP hatalarını throw etmez, 404 dönmüş olsa bile fetch başarıyla tamamlanmış bir Promise döndürür, çünkü bağlantı kurulmuştur.
    if (!response.ok) {
      await this.handleApiException(response);
    }
    return response.text() as unknown as Promise<string>;
  }

  async post<T>(
    url: string,
    data: unknown,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await this.middleware.fetch(url, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await this.handleApiException(response);
    }

    if (response.ok && response.status === 200) {
      let message = i18n.t("api.success");
      if (url.includes("/authentication/login")) {
        message = "Hoşgeldin";
      }
      if (url.includes("/authentication/logout")) {
        message = "Görüşürüz";
      }
      eventBus.emit("notify", {
        type: "success",
        message,
      });
    }

    return response.json() as Promise<T>;
  }

  async put<T>(
    url: string,
    data: unknown,
    options: RequestInit = {},
  ): Promise<T> {
    const response = await this.middleware.fetch(url, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      await this.handleApiException(response);
    }
    if (response.ok && response.status === 200) {
      eventBus.emit("notify", {
        type: "success",
        message: i18n.t("api.updated"),
      });
    }
    return response.json() as Promise<T>;
  }

  // fallbackFileName: Content-Disposition okunamadığında kullanılacak ad. Header'ın
  // JS'e açılması sunucunun Access-Control-Expose-Headers listesine bağlıdır ve araya
  // giren bir proxy de header'ı düşürebilir. Çağıran taraf anlamlı bir ad üretebiliyorsa
  // versin, yoksa dosya "download" olarak iner.
  async downloadBlob(
    url: string,
    options: RequestInit = {},
    fallbackFileName?: string,
  ): Promise<{ blob: Blob; fileName: string }> {
    const response = await this.middleware.fetch(url, {
      ...options,
      method: "GET",
    });

    if (!response.ok) {
      await this.handleApiException(response);
    }

    const blob = await response.blob();

    // Dosya adını header'dan al
    const contentDisposition = response.headers.get("content-disposition");

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (fileNameMatch && fileNameMatch[1]) {
        const headerFileName = fileNameMatch[1].replace(/['"]/g, "").trim();
        if (headerFileName) {
          return { blob, fileName: headerFileName };
        }
      }
    }

    if (fallbackFileName) {
      return { blob, fileName: fallbackFileName };
    }

    const contentType = response.headers.get("content-type");
    let fileName = "download";
    if (contentType?.includes("zip")) {
      fileName = "download.zip";
    } else if (contentType?.includes("pdf")) {
      fileName = "download.pdf";
    }

    return { blob, fileName };
  }

  async delete<T>(
    url: string,
    data?: unknown,
    options: RequestInit = {},
  ): Promise<T> {
    const requestOptions: RequestInit = {
      ...options,
      method: "DELETE",
    };

    // Add body if data is provided
    if (data !== undefined) {
      requestOptions.headers = {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      };
      requestOptions.body = JSON.stringify(data);
    }

    const response = await this.middleware.fetch(url, requestOptions);

    if (!response.ok) {
      await this.handleApiException(response);
    }

    if (response.ok && (response.status === 200 || response.status === 204)) {
      eventBus.emit("notify", {
        type: "success",
        message: i18n.t("api.deleted"),
      });
    }

    // Handle cases where DELETE returns no content (204)
    if (response.status === 204) {
      return {} as T;
    }

    return response.json() as Promise<T>;
  }

  async handleApiException(response: Response): Promise<never> {
    let apiMessage = "";
    if (response.status === 422) {
      const apiResponse = (await response.json()) as ApiResponse<unknown>;
      apiMessage = apiResponse.error?.errorMessage || "";
    }
    errorHandler.handleHttpError(response.status, {}, apiMessage);
    throw new HttpError(
      response.status,
      apiMessage || `HTTP Error: ${response.status}`,
    );
  }
}

export const api = new ApiClient(`${API_CONFIG.BASE_URL}`);

export const apiEndpoints = {
  getHtmlFromBlob: (url: string) => api.getHtmlFromBlob(url),

  getHomePageData: () =>
    api.get<ApiResponse<HomepageLoaderData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/homepages/user`,
    ),

  getAdminDashboardData: () =>
    api.get<ApiResponse<AdminPanelDashboardLoaderData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/dashboard`,
    ),

  getUserData: () =>
    api.get<ApiResponse<UserInformationData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users/information`,
    ),

  accountInfo: () =>
    api.get<ApiResponse<studentAccountInfo>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/students/by-session`,
    ),

  adminCheck: () =>
    api.get<boolean>(`${API_CONFIG.BASE_URL}/api/admin/protected`),

  profile: () =>
    api.get<ApiResponse<studentAccountInfo>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/students/by-session`,
    ),

  profileChanger: (input: studentAccountInfo) =>
    api.post<ApiResponse<studentAccountInfo>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/students/by-session`,
      input,
    ),

  course: (courseId: string) =>
    api.get<ApiResponse<CourseDetail>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}`,
    ),

  courseDetail: (courseId: string) =>
    api.get<ApiResponse<CourseDetail>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}/detail`,
    ),

  getPublicCourseDetail: (courseId: string) =>
    api.get<ApiResponse<CourseDetail>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}/public/detail`,
    ),

  courseWithContents: (courseId: string) =>
    api.get<ApiResponse<CourseLearningDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}/with-contents`,
    ),

  getCoursesPaginated: (request: CoursesFilter) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<CourseDto>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/paginated?${params}`,
    );
  },

  getPublicCoursesPaginated: (request: CoursesFilter) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<CourseDto>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/public/paginated?${params}`,
    );
  },

  checkLecturePublicAccess: (lectureId: string) =>
    api.get<ApiResponse<{ isPublic: boolean }>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lectures/${lectureId}/isPublic`,
    ),

  getCourseSidebarData: (courseId: string) =>
    api.get<ApiResponse<CourseSidebar>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}/sidebar`,
    ),

  getPublicCourseSidebarData: (courseId: string) =>
    api.get<ApiResponse<CourseSidebar>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}/public/sidebar`,
    ),

  getLectureContent: (lectureId: string) =>
    api.get<ApiResponse<LectureContent>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lectures/${lectureId}`,
    ),

  getPublicLectureContent: (lectureId: string) =>
    api.get<ApiResponse<LectureContent>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lectures/${lectureId}/public`,
    ),

  getMyCourses: () => {
    return api.get<ApiResponse<MyCoursesCourseCardDto[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/my-courses`,
    );
  },

  getCategoriesForFilter: () =>
    api.get<ApiResponse<BaseFilterOptionResponse[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories/filter-options`,
    ),

  getPublicCategoriesForFilter: () =>
    api.get<ApiResponse<BaseFilterOptionResponse[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories/public/filter-options`,
    ),

  getTagsForFilter: () =>
    api.get<ApiResponse<BaseFilterOptionResponse[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags/filter-options`,
    ),

  getPublicTagsForFilter: () =>
    api.get<ApiResponse<BaseFilterOptionResponse[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags/public/filter-options`,
    ),

  getMaxCoursePrice: () =>
    api.get<ApiResponse<number>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/max-price`,
    ),

  getPublicMaxCoursePrice: () =>
    api.get<ApiResponse<number>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/public/max-price`,
    ),

  loginData: (username: string, password: string) =>
    api.post<ApiResponse<TokenResponse>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/authentication/login`,
      { username, password },
    ),

  logout: () =>
    api.post<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/authentication/logout`,
      {},
    ),

  refresh: (input: RegisterFormData) =>
    api.post<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/authentication/register`,
      input,
    ),

  getAllBookmarkGroups: () =>
    api.get<ApiResponse<BookmarkGroupData[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups`,
    ),

  getBookmarkGroupById: (id: string) =>
    api.get<ApiResponse<BookmarkGroupData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups/${id}`,
    ),

  createBookmarkGroup: (input: CreateBookmarkGroupRequest) =>
    api.post<ApiResponse<BookmarkGroupData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups`,
      input,
    ),

  addLectureToBookmarkGroup: (input: AddLectureToBookmarkGroupRequest) =>
    api.post<ApiResponse<BookmarkGroupData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups/add-lecture`,
      input,
    ),

  removeLectureFromBookmarkGroup: (
    input: RemoveLectureFromBookmarkGroupRequest,
  ) =>
    api.post<ApiResponse<BookmarkGroupData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups/remove-lecture`,
      input,
    ),

  updateBookmarkGroup: (input: UpdateBookmarkGroupRequest) =>
    api.put<ApiResponse<BookmarkGroupData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups`,
      input,
    ),

  deleteBookmarkGroup: (id: string) =>
    api.delete<ApiResponse<void>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/bookmark-groups/${id}`,
    ),

  addItemToBasket: (input: AddItemRequest) =>
    api.post<ApiResponse<BasketDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/baskets/add-item`,
      input,
    ),

  getBasket: (input: string | null) => {
    let requestUrl = `${API_CONFIG.BASE_URL}/api/v1/web/baskets`;
    if (input) {
      requestUrl += `?basketId=${input}`;
    }
    return api.get<ApiResponse<BasketDto>>(requestUrl);
  },

  removeItemFromBasket: (input: RemoveItemRequest) =>
    api.post<ApiResponse<BasketDto | null>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/baskets/remove-item`,
      input,
    ),

  applyDiscountCodeToBasket: (input: ApplyDiscountCodeRequest) =>
    api.post<ApiResponse<BasketDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/baskets/apply-discount-code`,
      input,
    ),
  applyDiscountCodeToBasketItemBasket: (
    input: ApplyDiscountCodeToBasketItemRequest,
  ) =>
    api.post<ApiResponse<BasketDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/baskets/apply-discount-code-to-basket-item`,
      input,
    ),

  removeDiscountCodeFromBasket: (input: RemoveDiscountCodeRequest) =>
    api.post<ApiResponse<BasketDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/baskets/remove-discount-code`,
      input,
    ),

  removeDiscountCodeFromBasketItemBasket: (
    input: RemoveDiscountCodeFromBasketItemRequest,
  ) =>
    api.post<ApiResponse<BasketDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/baskets/remove-discount-code-from-basket-item`,
      input,
    ),

  createFreeOrder: (input: CreateFreeOrderRequest) =>
    api.post<ApiResponse<baseCreateResponse<string>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/orders/free`,
      input,
    ),

  getOrderSummary: (orderId: string) =>
    api.get<ApiResponse<OrderSummaryDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/orders/${orderId}/summary`,
    ),

  getUserOrders: () =>
    api.get<ApiResponse<OrderHistoryDto[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/orders/user-orders`,
    ),

  initializePayment: (input: PaymentInitRequest) =>
    api.post<ApiResponse<PaymentInitResponse>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/payment/initialize`,
      input,
    ),

  getPaymentStatus: (orderId: string) =>
    api.get<ApiResponse<PaymentStatusDto>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/payment/${orderId}/status`,
    ),

  changePassword: (input: changePasswordRequest) =>
    api.post<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/authentication/change-password`,
      input,
    ),

  refreshAccess: (refreshToken: string) =>
    api.post<ApiResponse<TokenResponse>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/authentication/refresh-token`,
      { refreshToken },
    ),

  sendResetPasswordEmail: async (
    email: string,
  ): Promise<ApiResponse<unknown>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: "Password reset email sent successfully.",
          data: {},
          error: null,
          wrapped: true,
        });
      }, 1000); // simulate 1 second network delay
    });
  },
  sendVerifyCodeAndEmail: async (
    data: VerifyCodeFormData,
  ): Promise<ApiResponse<unknown>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: "Password reset email sent successfully.",
          data: {},
          error: null,
          wrapped: true,
        });
      }, 1000); // simulate 1 second network delay
    });
  },
  resetPassword: async (
    data: ResetPasswordDTO,
  ): Promise<ApiResponse<unknown>> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: "Password reset email sent successfully.",
          data: {},
          error: null,
          wrapped: true,
        });
      }, 1000); // simulate 1 second network delay
    });
  },
  // lecturerCreateCourse: (data: CourseSubmissionData) =>
  //   api.post<ApiResponse<baseCreateResponse<string>>>(
  //     `${API_CONFIG.BASE_URL}/api/v1/web/courses`,
  //     data
  //   ),

  lecturerGetCourses: (request: LecturerGetCoursesRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<lecturerManageCoursesLoaderDTO>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/lecturer-courses?${params}`,
    );
  },
  lecturerGetOrders: (request: LecturerGetOrdersRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<LecturerOrderItem>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lecturer/orders/paginated?${params}`,
    );
  },
  lecturerGetCourseContent: (courseId: string) =>
    api.get<ApiResponse<ManageCourseData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}/contents`,
    ),
  lecturerGetCourseInfo: (courseId: string) =>
    api.get<ApiResponse<lecturerManageCourseLoaderDTO>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${courseId}`,
    ),
  lecturerGetSectionContent: (id: string) =>
    api.get<ApiResponse<ManageCourseUpdateSectionLoaderDTO>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/sections/${id}`,
    ),
  lecturerGetLectureContent: (id: string) =>
    api.get<ApiResponse<ManageCourseUpdateLectureLoaderDTO>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lectures/${id}`,
    ),
  lecturerReOrderSection: (input: reOrderDataDTO) =>
    api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/sections/move-section`,
      input,
    ),
  reOrderLecture: (input: reOrderDataDTO) =>
    api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lectures/move-lecture`,
      input,
    ),
  lecturerCreateSection: (
    manageCourseCreateSectionDTO: ManageCourseCreateSectionDTO,
  ) =>
    api.post<ApiResponse<baseCreateResponse<string>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/sections`,
      manageCourseCreateSectionDTO,
    ),
  lecturerUpdateSection: (input: ManageCourseUpdateSectionDTO) =>
    api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/sections`,
      input,
    ),
  lecturerDeleteCourse: (id: string) =>
    api.delete<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${id}`,
    ),
  lecturerDeleteSection: (id: string) =>
    api.delete<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/sections/${id}`,
    ),
  lecturerDeleteLecture: (id: string) =>
    api.delete<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lectures/${id}`,
    ),
  getAllCategories: () =>
    api.get<ApiResponse<lecturerCreateCourseCategory[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories`,
    ),
  getAllTags: () =>
    api.get<ApiResponse<lecturerCreateCourseTag[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags`,
    ),
  getCategories: (request: BasePaginatedRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<AdminPanelCategoryData>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories/paginated?${params}`,
    );
  },

  getCategoryById: (id: string) => {
    return api.get<ApiResponse<AdminPanelCategoryData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories/${id}`,
    );
  },

  updateCategory: (request: AdminPanelCategoryData) => {
    return api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories`,
      request,
    );
  },

  createCategory: (request: AdminPanelCategoryCreateData) => {
    return api.post<ApiResponse<baseCreateResponse<string>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories`,
      request,
    );
  },

  deleteCategory: (id: string) => {
    return api.delete<ApiResponse<AdminPanelCategoryData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/categories/${id}`,
    );
  },

  getTags: (request: BasePaginatedRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<AdminPanelTagData>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags/paginated?${params}`,
    );
  },

  getTagById: (id: string) => {
    return api.get<ApiResponse<AdminPanelTagData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags/${id}`,
    );
  },

  updateTag: (request: AdminPanelTagData) => {
    return api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags`,
      request,
    );
  },

  createTag: (request: AdminPanelTagCreateData) => {
    return api.post<ApiResponse<baseCreateResponse<string>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags`,
      request,
    );
  },

  deleteTag: (id: string) => {
    return api.delete<ApiResponse<AdminPanelTagData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/tags/${id}`,
    );
  },

  getLecturerCoursesForDiscountCode: () => {
    return api.get<ApiResponse<CoursesForDiscountCodeDto[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/lecturer-courses-for-discount-code`,
    );
  },

  getLecturerDiscountCodes: (request: BasePaginatedRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<DiscountCodeData>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lecturer/discount-codes/paginated?${params}`,
    );
  },

  getLecturerDiscountCodeById: (id: string) => {
    return api.get<ApiResponse<DiscountCodeData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lecturer/discount-codes/${id}`,
    );
  },

  updateLecturerDiscountCode: (request: DiscountCodeData) => {
    return api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lecturer/discount-codes`,
      request,
    );
  },

  createLecturerDiscountCode: (request: DiscountCodeCreateData) => {
    return api.post<ApiResponse<baseCreateResponse<string>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lecturer/discount-codes`,
      request,
    );
  },

  deleteLecturerDiscountCode: (id: string) => {
    return api.delete<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/lecturer/discount-codes/${id}`,
    );
  },

  getAdminDiscountCodes: (request: BasePaginatedRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<
      ApiResponse<BasePaginatedResponse<AdminPanelDiscountCodeData>>
    >(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/discount-codes/paginated?${params}`,
    );
  },

  getAdminDiscountCodeById: (id: string) => {
    return api.get<ApiResponse<AdminPanelDiscountCodeData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/discount-codes/${id}`,
    );
  },

  updateAdminDiscountCode: (request: AdminPanelDiscountCodeData) => {
    return api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/discount-codes`,
      request,
    );
  },

  createAdminDiscountCode: (request: AdminPanelDiscountCodeCreateData) => {
    return api.post<ApiResponse<baseCreateResponse<string>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/discount-codes`,
      request,
    );
  },

  deleteAdminDiscountCode: (id: string) => {
    return api.delete<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/discount-codes/${id}`,
    );
  },
  adminGetAllCourses: (request: LecturerGetCoursesRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<lecturerManageCoursesLoaderDTO>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/manage-courses-in-detail?${params}`,
    );
  },
  getAdminManageCourses: (request: BasePaginatedRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<PagingResultModel<AdminManageCourseResponse>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/admin/manage-courses?${params}`,
    );
  },

  adminDeleteCourse: (id: string) =>
    api.delete<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/${id}`,
    ),

  adminUpdateCoursePublishStatus: (id: string, publishStatus: number) =>
    api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/courses/change-publish`,
      { id, publishStatus },
    ),

  getUsers: (request: BasePaginatedRequest) => {
    const params = apiEndpoints.objectToSearchParams(request);
    return api.get<ApiResponse<BasePaginatedResponse<AdminPanelUserData>>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users/paginated?${params}`,
    );
  },

  getUserById: (id: string) => {
    return api.get<ApiResponse<AdminPanelUserData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users/${id}`,
    );
  },

  updateUser: (request: AdminPanelUserData) => {
    return api.put<ApiResponse<boolean>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users`,
      request,
    );
  },

  deleteUser: (id: string) => {
    return api.delete<ApiResponse<AdminPanelUserData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users/${id}`,
    );
  },

  markLectureComplete: (lectureId: string) =>
    api.post<
      ApiResponse<{
        lectureId: string;
        isCompleted: boolean;
        completedAt: string;
      }>
    >(`${API_CONFIG.BASE_URL}/api/v1/web/lectures/${lectureId}/complete`, {}),

  markLectureIncomplete: (lectureId: string) =>
    api.post<
      ApiResponse<{
        lectureId: string;
        isCompleted: boolean;
        completedAt: string | null;
      }>
    >(`${API_CONFIG.BASE_URL}/api/v1/web/lectures/${lectureId}/incomplete`, {}),

  // Certificates
  getMyCertificates: () =>
    api.get<ApiResponse<CertificateResponse[]>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/certificates`,
    ),

  getCourseCertificate: (courseId: string) =>
    api.get<ApiResponse<CertificateResponse>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/certificates/course/${courseId}`,
    ),

  issueCertificate: (courseId: string) =>
    api.post<ApiResponse<CertificateResponse>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/certificates/course/${courseId}/issue`,
      {},
    ),

  downloadCourseCertificates: (courseId: string) =>
    api.downloadBlob(
      `${API_CONFIG.BASE_URL}/api/v1/web/certificates/course/${courseId}/download`,
    ),

  objectToSearchParams: <T extends object>(obj: T): string => {
    const params = new URLSearchParams();

    Object.entries(obj).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null) {
            params.append(key, String(v));
          }
        });
      } else if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    return params.toString();
  },

  // Admin only: user record together with identity / parent / address information.
  getUserDetailById: (id: string) =>
    api.get<ApiResponse<AdminPanelUserDetailData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users/${id}/detail`,
    ),

  // Admin only: courses the user bought as a student and the ones they teach.
  getUserCourses: (id: string) =>
    api.get<ApiResponse<UserCoursesData>>(
      `${API_CONFIG.BASE_URL}/api/v1/web/users/${id}/courses`,
    ),

  // Admin only: accounting sales list for a date range. The backend returns a raw
  // xlsx file instead of the ApiResponse envelope, so this goes through downloadBlob.
  // from / to are inclusive days in "yyyy-MM-dd" format.
  // Fallback adı backend'in ürettiğiyle birebir aynı; Content-Disposition okunabildiğinde
  // header kazanır, okunamadığında dosya yine doğru adla iner.
  downloadSalesReport: (from: string, to: string) =>
    api.downloadBlob(
      `${API_CONFIG.BASE_URL}/api/v1/web/reports/sales/download?${new URLSearchParams(
        { from, to },
      ).toString()}`,
      {},
      `Codeline-Satis-Raporu_${from}_${to}.xlsx`,
    ),
};
